import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Zap, Check, Loader2, Building2, ArrowRight, Sparkles, ChevronDown, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/authStore';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  getUserCurrency,
  setUserCurrency,
  formatPrice,
  getAvailableCurrencies,
  type CurrencyInfo,
} from '@/lib/currency';
import api from '@/services/api';

interface TrialExpiredModalProps {
  isOpen: boolean;
  onPlanSelected: (addCompanyIntelligence?: boolean) => void;
}

type Step = 'choose-plan' | 'company-intelligence';
type PlanKey = 'basic' | 'basic_plus' | 'pro';
type BillingCycle = 'monthly' | 'quarterly' | 'yearly';

interface PlanInfo {
  key: PlanKey;
  name: string;
  icon: typeof Zap;
  price: { monthly: number; quarterly: number; yearly: number };
  description: string;
  features: string[];
  highlighted?: boolean;
  badge?: string;
}

const plans: PlanInfo[] = [
  {
    key: 'basic',
    name: 'Basic',
    icon: Zap,
    price: { monthly: 10, quarterly: 27, yearly: 102 },
    description: 'Perfect for getting started',
    features: [
      '5 generations/month',
      '1 platform per generation',
      '1 workspace',
      '5 saved prompts',
      'Post to LinkedIn, Instagram, Facebook',
      'AI Profile Intelligence',
      'AI Learning System',
    ],
  },
  {
    key: 'basic_plus',
    name: 'Basic+',
    icon: Sparkles,
    price: { monthly: 25, quarterly: 67.5, yearly: 255 },
    description: 'For growing creators',
    highlighted: true,
    badge: 'Most Popular',
    features: [
      '15 generations/month',
      '2 platforms per generation',
      '2 variations per platform',
      '3 workspaces',
      '15 saved prompts',
      '3 stock images per generation',
      '15 AI images/month',
      'Service-specific posts',
    ],
  },
  {
    key: 'pro',
    name: 'Pro',
    icon: Crown,
    price: { monthly: 50, quarterly: 135, yearly: 510 },
    description: 'Full power for professionals',
    features: [
      '50 generations/month',
      'All 4 platforms',
      '3 variations per platform',
      '8 workspaces',
      '70 saved prompts',
      '5 stock images per generation',
      '30 AI images/month (Context-aware)',
      'Post to all platforms including X',
    ],
  },
];

export default function TrialExpiredModal({ isOpen, onPlanSelected }: TrialExpiredModalProps) {
  const navigate = useNavigate();
  const { logout, user } = useAuthStore();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanKey | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [currency, setCurrency] = useState<CurrencyInfo>(getUserCurrency());
  const [step, setStep] = useState<Step>('choose-plan');
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponStatus, setCouponStatus] = useState<{ valid: boolean; message: string } | null>(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [dialogHidden, setDialogHidden] = useState(false);

  // Load Razorpay script
  useEffect(() => {
    if (document.getElementById('razorpay-script')) {
      setRazorpayLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => setRazorpayLoaded(true);
    script.onerror = () => {
      console.error('Failed to load Razorpay script');
      setError('Payment system failed to load. Please refresh the page.');
    };
    document.body.appendChild(script);
  }, []);

  const handleCurrencyChange = (newCurrency: CurrencyInfo) => {
    setCurrency(newCurrency);
    setUserCurrency(newCurrency.code);
  };

  const getPrice = (plan: PlanInfo) => {
    const usdPrice = plan.price[billingCycle];
    return formatPrice(usdPrice, currency);
  };

  const getPeriodLabel = () => {
    switch (billingCycle) {
      case 'monthly': return 'month';
      case 'quarterly': return 'quarter';
      case 'yearly': return 'year';
    }
  };

  const currencies = useMemo(() => {
    const popular = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD'];
    const all = getAvailableCurrencies();
    const popularCurrencies = popular.map(code => all.find(c => c.code === code)!).filter(Boolean);
    const otherCurrencies = all.filter(c => !popular.includes(c.code));
    return [...popularCurrencies, ...otherCurrencies];
  }, []);

  const handleSelectPlan = useCallback(async () => {
    if (!selectedPlan) return;

    if (!razorpayLoaded) {
      setError('Payment system is loading. Please wait a moment and try again.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Create Razorpay subscription
      const subscriptionData = await api.createSubscription(selectedPlan, billingCycle, couponCode || undefined);

      // =======================================================================
      // ADMIN BYPASS: If subscription_id is "admin_free_access", tier is already changed
      // =======================================================================
      if (subscriptionData.subscription_id === 'admin_free_access') {
        // Admin - tier already changed, proceed to next step
        if (selectedPlan === 'pro' || selectedPlan === 'basic_plus') {
          setStep('company-intelligence');
        } else {
          onPlanSelected(false);
        }
        setIsProcessing(false);
        return;
      }

      // =======================================================================
      // Regular users: Open Razorpay checkout
      // =======================================================================
      const planForHandler = selectedPlan;
      const cycleForHandler = billingCycle;

      const options = {
        key: subscriptionData.key_id,
        subscription_id: subscriptionData.subscription_id,
        name: 'Zupa',
        description: subscriptionData.plan_name,
        prefill: {
          name: subscriptionData.user_name,
          email: subscriptionData.user_email,
        },
        theme: {
          color: '#D97757',
        },
        handler: async (response: any) => {
          // Verify subscription payment on backend
          try {
            const result = await api.verifySubscription({
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              tier: planForHandler,
              billing_cycle: cycleForHandler,
            });

            if (result.success) {
              // For Pro/Basic+, show company intelligence question
              if (planForHandler === 'pro' || planForHandler === 'basic_plus') {
                setStep('company-intelligence');
              } else {
                onPlanSelected(false);
              }
            } else {
              setError(result.message || 'Payment verification failed');
            }
          } catch (err: any) {
            setError(err.response?.data?.detail || 'Payment verification failed');
          } finally {
            setIsProcessing(false);
          }
        },
        modal: {
          ondismiss: () => {
            setDialogHidden(false);
            setIsProcessing(false);
          },
        },
      };

      // Hide the dialog overlay so Razorpay modal is fully interactive
      setDialogHidden(true);

      // Small delay for DOM update before opening Razorpay
      await new Promise(resolve => setTimeout(resolve, 100));

      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', () => {
        setDialogHidden(false);
        setIsProcessing(false);
        setError('Payment failed. Please try again.');
      });
      razorpay.open();

    } catch (err: any) {
      console.error('Failed to initiate payment:', err);
      setDialogHidden(false);
      setError(err.response?.data?.detail || 'Failed to initiate payment. Please try again.');
      setIsProcessing(false);
    }
  }, [selectedPlan, billingCycle, couponCode, razorpayLoaded, onPlanSelected]);

  const handleCompanyIntelligenceChoice = (addNow: boolean) => {
    onPlanSelected(addNow);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <Dialog open={isOpen && !dialogHidden} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-4xl mx-4 max-h-[90vh] overflow-y-auto [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {step === 'choose-plan' ? (
          <>
            <DialogHeader className="text-center">
              <DialogTitle className="text-2xl md:text-3xl">
                Your Trial Has Ended
              </DialogTitle>
              <DialogDescription className="text-base">
                Choose a plan to continue creating psychology-powered content with Zupa
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              {/* Error message */}
              {error && (
                <div className="mb-4 p-3 bg-error-bg dark:bg-red-900/20 border border-error-border dark:border-red-800 rounded-lg text-error-text dark:text-red-300 text-sm">
                  {error}
                </div>
              )}

              {/* Billing cycle and currency controls */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                <div className="inline-flex items-center bg-paper dark:bg-gray-800 rounded-full p-1">
                  {(['monthly', 'quarterly', 'yearly'] as const).map((cycle) => (
                    <button
                      key={cycle}
                      onClick={() => setBillingCycle(cycle)}
                      className={cn(
                        'px-3 py-1.5 text-sm font-medium rounded-full transition-all',
                        billingCycle === cycle
                          ? 'bg-white dark:bg-gray-700 text-primary shadow-sm'
                          : 'text-muted-foreground hover:text-primary'
                      )}
                    >
                      {cycle === 'monthly' && 'Monthly'}
                      {cycle === 'quarterly' && 'Quarterly'}
                      {cycle === 'yearly' && 'Yearly'}
                    </button>
                  ))}
                </div>
                
                {billingCycle !== 'monthly' && (
                  <span className="text-sm text-ink dark:text-green-400 font-medium">
                    {billingCycle === 'quarterly' ? 'Save 10%' : 'Save 15%'}
                  </span>
                )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1 text-xs">
                      {currency.code}
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="max-h-48 overflow-y-auto">
                    {currencies.map((c) => (
                      <DropdownMenuItem
                        key={c.code}
                        onClick={() => handleCurrencyChange(c)}
                        className={cn(
                          'cursor-pointer text-xs',
                          c.code === currency.code && 'bg-accent'
                        )}
                      >
                        {c.code} ({c.symbol})
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Plans grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {plans.map((plan) => {
                  const Icon = plan.icon;
                  const isSelected = selectedPlan === plan.key;
                  
                  return (
                    <button
                      key={plan.key}
                      onClick={() => setSelectedPlan(plan.key)}
                      disabled={isProcessing}
                      className={cn(
                        'relative text-left p-4 rounded-lg border-2 transition-all',
                        isSelected 
                          ? 'border-accent bg-accent/5' 
                          : plan.highlighted
                            ? 'border-accent/50 hover:border-accent'
                            : 'border-border hover:border-accent/50',
                        isProcessing && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      {/* Badge */}
                      {plan.badge && (
                        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                          <span className="bg-accent text-accent-foreground text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                            {plan.badge}
                          </span>
                        </div>
                      )}

                      {/* Header */}
                      <div className="flex items-center gap-2 mb-3">
                        <div className="p-1.5 rounded-lg bg-paper dark:bg-gray-800">
                          <Icon className="w-4 h-4 text-ink dark:text-steel" />
                        </div>
                        <div>
                          <h3 className="font-heading font-bold text-primary text-sm">
                            {plan.name}
                          </h3>
                        </div>
                      </div>

                      {/* Price */}
                      <div className="mb-3">
                        <span className="text-2xl font-heading font-bold text-primary">
                          {getPrice(plan)}
                        </span>
                        <span className="text-muted-foreground text-sm">
                          /{getPeriodLabel()}
                        </span>
                      </div>

                      {/* Features */}
                      <ul className="space-y-1.5">
                        {plan.features.slice(0, 5).map((feature) => (
                          <li key={feature} className="flex items-start gap-2 text-xs">
                            <Check className="w-3 h-3 text-ink mt-0.5 shrink-0" />
                            <span className="text-muted-foreground">{feature}</span>
                          </li>
                        ))}
                        {plan.features.length > 5 && (
                          <li className="text-xs text-muted-foreground pl-5">
                            +{plan.features.length - 5} more features
                          </li>
                        )}
                      </ul>

                      {/* Selection indicator */}
                      {isSelected && (
                        <div className="absolute top-3 right-3 w-5 h-5 bg-accent rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Coupon Code */}
              <div className="mb-4">
                <label className="text-sm font-medium text-muted-foreground mb-1 block">
                  Coupon Code (optional)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value);
                      setCouponStatus(null);
                    }}
                    placeholder="Enter coupon code"
                    className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-background text-primary placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!couponCode.trim() || isValidatingCoupon}
                    onClick={async () => {
                      setIsValidatingCoupon(true);
                      try {
                        const result = await api.validateCoupon(couponCode.trim());
                        setCouponStatus(result);
                      } catch {
                        setCouponStatus({ valid: false, message: 'Failed to validate coupon.' });
                      }
                      setIsValidatingCoupon(false);
                    }}
                    className="shrink-0"
                  >
                    {isValidatingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                  </Button>
                </div>
                {couponStatus && (
                  <p className={cn(
                    'text-xs mt-1',
                    couponStatus.valid ? 'text-stamp' : 'text-error-text'
                  )}>
                    {couponStatus.valid ? '✓' : '✗'} {couponStatus.message}
                  </p>
                )}
              </div>

              {/* Continue button */}
              <Button
                onClick={handleSelectPlan}
                disabled={!selectedPlan || isProcessing || !razorpayLoaded}
                className="w-full bg-accent hover:bg-accent/90"
                size="lg"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : !razorpayLoaded ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading payment...
                  </>
                ) : selectedPlan ? (
                  `Subscribe to ${plans.find(p => p.key === selectedPlan)?.name}`
                ) : (
                  'Select a plan to continue'
                )}
              </Button>

              {/* Enterprise link */}
              <p className="text-center text-sm text-muted-foreground mt-4">
                Need more? <a href="mailto:bd@summerhilltechnologies.com" className="text-accent hover:underline">Contact us for Enterprise</a>
              </p>

              {/* Sign out / switch account */}
              <div className="mt-6 pt-4 border-t border-border text-center">
                {user?.email && (
                  <p className="text-xs text-muted-foreground mb-2">
                    Signed in as {user.email}
                  </p>
                )}
                <button
                  type="button"
                  onClick={handleLogout}
                  disabled={isProcessing}
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  Sign out & use a different account
                </button>
              </div>
            </div>
          </>
        ) : (
          /* Company Intelligence Step */
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl text-center flex items-center justify-center gap-2">
                <Crown className="w-6 h-6 text-ink" />
                Welcome to {selectedPlan === 'pro' ? 'Pro' : 'Basic+'}!
              </DialogTitle>
              <DialogDescription className="text-center text-base">
                Would you like to set up Company Intelligence?
              </DialogDescription>
            </DialogHeader>

            <div className="py-6">
              <div className="bg-paper dark:bg-amber-900/20 border border-hairline dark:border-amber-800 rounded-lg p-6 mb-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-white dark:bg-amber-900/30 rounded-md shrink-0 border border-hairline">
                    <Building2 className="w-6 h-6 text-ink dark:text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-ink dark:text-amber-100 mb-2">
                      Company Intelligence
                    </h3>
                    <p className="text-sm text-steel-dark dark:text-amber-200 mb-3">
                      Let AI research your company and create personalized content that matches your brand voice and target audience.
                    </p>
                    <ul className="text-sm text-steel-dark dark:text-amber-300 space-y-1">
                      <li>• AI analyzes your website and brand</li>
                      <li>• Generates content tailored to your business</li>
                      <li>• Learns from your favorite posts over time</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleCompanyIntelligenceChoice(false)}
                >
                  Add Later
                </Button>
                <Button
                  className="flex-1 bg-cargo text-cargo-ink hover:bg-cargo hover:opacity-90"
                  onClick={() => handleCompanyIntelligenceChoice(true)}
                >
                  Set Up Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
